import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import { useContext, useRef } from "react"
import ScrollLock from "react-scrolllock-with-horizontal-scroll"

import withHeader from "areas/header/hoc/with-header"
import MobileMenu from "areas/header/mobile-menu"
import { HeaderMenuContext } from "areas/header/portal"
import Button from "components/ui/button"
import Divider from "components/ui/divider"
import { retry } from "helpers/application"
import useAriaHidden from "helpers/use-aria-hidden"
import { useNavigation } from "helpers/use-navigation"

import OverLay from "../overlay"

const CountrySwitcher = loadable(() =>
  retry(() => import("./country-switcher"))
)
const MobileNav = loadable(() => retry(() => import("./mobile-nav")))
const MobileSubmenu = loadable(() => retry(() => import("./mobile-submenu")))

const MobileMenuContent = ({ showMenu, toggleMenu }) => {
  const { menu } = useContext(HeaderMenuContext)
  const menuRef = useRef()
  const keyboardProps = useNavigation({
    onEscape: () => toggleMenu(showMenu),
  })

  const menuItems = menu && menu.categories
  const navBarLinks = menu?.navbar_links?.filter((links) => !links.external)
  const externalLinks = menu?.navbar_links?.filter((links) => links.external)

  useAriaHidden({ ref: menuRef })

  return (
    <MobileMenu id="mobile-menu" ref={menuRef} {...keyboardProps}>
      <ScrollLock>
        <div className="side-menu-content">
          <nav className="px-16 pt-24">
            <ul aria-label="Main Navigation" className="list-none" role="menu">
              {menuItems?.length > 0 && <MobileNav items={menuItems} />}
              <Divider className="bg-black-print my-0" />
              {navBarLinks?.length > 0 && <MobileNav items={navBarLinks} />}
              <CountrySwitcher />
              {externalLinks?.map((link, i) => (
                <li
                  data-cy={`nav-mobile-${link.name}`}
                  key={`external-link-${i}`}
                  role="none"
                >
                  <Button
                    asChild
                    className="mt-24 uppercase"
                    size="sm"
                    variant="secondary"
                  >
                    <a
                      data-lc="menu-mobile"
                      href={link.url}
                      rel="noopener noreferrer"
                      role="menuitem"
                      target="_blank"
                    >
                      {link.name}
                    </a>
                  </Button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <MobileSubmenu />
      </ScrollLock>
    </MobileMenu>
  )
}

MobileMenuContent.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

const InternalMobileMenu = ({ showMenu, toggleMenu }) => (
  <AnimatePresence>
    {showMenu && (
      <>
        <FocusScope autoFocus contain restoreFocus>
          <MobileMenuContent showMenu={showMenu} toggleMenu={toggleMenu} />
        </FocusScope>
        <OverLay />
      </>
    )}
  </AnimatePresence>
)

InternalMobileMenu.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

export default withHeader(InternalMobileMenu)
